/**
 * Small raw-WebGL2 helpers: program compilation with cached uniform locations, render targets,
 * and the full-screen triangle every pass draws with.
 *
 * Deviation from PLAN.md D6: we use raw WebGL2 instead of React Three Fiber / three.js to keep the
 * dependency surface small. Everything the plan describes (ping-pong FBOs, one big GLSL pass, MRT
 * emissive + dual-Kawase bloom, GL point particles) is done here by hand.
 */

export interface Program {
  readonly program: WebGLProgram;
  /** cached uniform locations (null for uniforms the compiler optimised away) */
  loc(name: string): WebGLUniformLocation | null;
  use(): void;
  destroy(): void;
}

/** Insert `#define`s right after the `#version` line (which must stay the very first line). */
export function withDefines(source: string, defines: readonly string[]): string {
  if (defines.length === 0) return source;
  const nl = source.indexOf('\n');
  const head = source.slice(0, nl + 1);
  const body = source.slice(nl + 1);
  return head + defines.map((d) => `#define ${d}\n`).join('') + body;
}

function compileShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const sh = gl.createShader(type);
  if (!sh) throw new Error('createShader failed');
  gl.shaderSource(sh, source);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS) && !gl.isContextLost()) {
    const log = gl.getShaderInfoLog(sh) ?? '';
    gl.deleteShader(sh);
    throw new Error(`shader compile failed:\n${log}\n---\n${numbered(source)}`);
  }
  return sh;
}

function numbered(src: string): string {
  return src.split('\n').map((l, i) => `${String(i + 1).padStart(4)}: ${l}`).join('\n');
}

export function createProgram(gl: WebGL2RenderingContext, vert: string, frag: string, defines: readonly string[] = []): Program {
  const vs = compileShader(gl, gl.VERTEX_SHADER, withDefines(vert, defines));
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, withDefines(frag, defines));
  const program = gl.createProgram();
  if (!program) throw new Error('createProgram failed');
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS) && !gl.isContextLost()) {
    const log = gl.getProgramInfoLog(program) ?? '';
    gl.deleteProgram(program);
    throw new Error(`program link failed: ${log}`);
  }
  const cache = new Map<string, WebGLUniformLocation | null>();
  const n = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS) as number;
  for (let i = 0; i < n; i++) {
    const info = gl.getActiveUniform(program, i);
    if (!info) continue;
    // arrays are reported as "name[0]"; cache both spellings
    const base = info.name.replace(/\[0\]$/, '');
    const loc = gl.getUniformLocation(program, info.name);
    cache.set(info.name, loc);
    cache.set(base, loc);
  }
  return {
    program,
    loc: (name) => cache.get(name) ?? null,
    use: () => gl.useProgram(program),
    destroy: () => gl.deleteProgram(program),
  };
}

export interface Target {
  fb: WebGLFramebuffer;
  textures: WebGLTexture[];
  width: number;
  height: number;
}

export interface TargetOptions {
  internalFormat: number;
  format: number;
  type: number;
  /** gl.LINEAR or gl.NEAREST */
  filter: number;
  /** number of colour attachments (MRT) */
  count?: number;
}

export function createTexture(gl: WebGL2RenderingContext, width: number, height: number, o: TargetOptions, data: ArrayBufferView | null = null): WebGLTexture {
  const tex = gl.createTexture();
  if (!tex) throw new Error('createTexture failed');
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, o.filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, o.filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, o.internalFormat, width, height, 0, o.format, o.type, data);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}

export function createTarget(gl: WebGL2RenderingContext, width: number, height: number, o: TargetOptions): Target {
  const count = o.count ?? 1;
  const fb = gl.createFramebuffer();
  if (!fb) throw new Error('createFramebuffer failed');
  const textures: WebGLTexture[] = [];
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  const bufs: number[] = [];
  for (let i = 0; i < count; i++) {
    const tex = createTexture(gl, width, height, o);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0 + i, gl.TEXTURE_2D, tex, 0);
    textures.push(tex);
    bufs.push(gl.COLOR_ATTACHMENT0 + i);
  }
  gl.drawBuffers(bufs);
  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  if (status !== gl.FRAMEBUFFER_COMPLETE && !gl.isContextLost()) {
    for (const t of textures) gl.deleteTexture(t);
    gl.deleteFramebuffer(fb);
    throw new Error(`framebuffer incomplete: 0x${status.toString(16)}`);
  }
  return { fb, textures, width, height };
}

export function destroyTarget(gl: WebGL2RenderingContext, t: Target | null | undefined): void {
  if (!t) return;
  for (const tex of t.textures) gl.deleteTexture(tex);
  gl.deleteFramebuffer(t.fb);
}

/** Whether a target with the given format can actually be rendered to (float targets need EXT_color_buffer_float). */
export function canRenderTo(gl: WebGL2RenderingContext, o: TargetOptions): boolean {
  try {
    const t = createTarget(gl, 4, 4, o);
    destroyTarget(gl, t);
    return true;
  } catch {
    return false;
  }
}

export interface FullscreenQuad {
  draw(): void;
  destroy(): void;
}

/** One big triangle covering clip space; the vertex shader derives vUv from the position. */
export function createFullscreenQuad(gl: WebGL2RenderingContext): FullscreenQuad {
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) throw new Error('VAO/VBO creation failed');
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    draw: () => {
      gl.bindVertexArray(vao);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      gl.bindVertexArray(null);
    },
    destroy: () => {
      gl.deleteBuffer(vbo);
      gl.deleteVertexArray(vao);
    },
  };
}

export function bindTextureUnit(gl: WebGL2RenderingContext, unit: number, tex: WebGLTexture | null, loc: WebGLUniformLocation | null): void {
  gl.activeTexture(gl.TEXTURE0 + unit);
  gl.bindTexture(gl.TEXTURE_2D, tex);
  if (loc) gl.uniform1i(loc, unit);
}
